/**
 * Route manifest for the ITR / Settings / misc slice of the S2 port.
 *
 * Exported as a `RouteObject[]` for the orchestrator to splice into
 * `src/routes.tsx` — this file never edits the route table itself, because
 * that table is GENERATED from `PAGE_REGISTRY` and must stay that way (a
 * hand-added route is a route with no permission check).
 *
 * Two shapes in here, for two different gates:
 *
 *  - `itrRoutes` are REGISTRY children. `/itr` is registry key `itr`, so the
 *    generated `itr/*` entry already carries the matrix `view` check; these
 *    are the elements that belong under it. Splice them in by replacing the
 *    generated element with `<ITRSection />`, or by registering `ITRPage` /
 *    `ITRDetailPage` in `routes.tsx`'s `IMPLEMENTED` map (see `itrSection`).
 *
 *  - `settingsRoutes` is NOT a registry page. `/settings` is a HARD ADMIN
 *    path in `middleware.ts`, and `lib/rbac/route-guard.ts::isAdminOnlyPath`
 *    already encodes exactly that — so mounting it as a child of the existing
 *    `RequireAuth` layout reproduces the middleware's behaviour precisely: an
 *    ADMIN renders it, a USER is redirected to `/no-access?page=admin`
 *    (SAFE-04). No extra guard component is needed, and adding one would risk
 *    diverging from the tested rule.
 */
import { Suspense, type ReactNode } from "react";
import { Route, Routes, type RouteObject } from "react-router-dom";
import { lazyNamed, RouteSkeleton } from "@/lib/prefetch";

const ITRPage = lazyNamed(() => import("@/pages/itr/ITRPage"), "ITRPage");
const ITRDetailPage = lazyNamed(() => import("@/pages/itr/ITRDetailPage"), "ITRDetailPage");
const SettingsPage = lazyNamed(() => import("@/pages/settings/SettingsPage"), "SettingsPage");

function s(element: ReactNode): ReactNode {
  return <Suspense fallback={<RouteSkeleton />}>{element}</Suspense>;
}

/**
 * The element for the generated `itr/*` registry route: `/itr` renders the
 * landing page, `/itr/:id` the detail page. Nested `<Routes>` because the
 * parent path is a splat, which is how every registry entry is generated.
 */
export function ITRSection() {
  return (
    <Routes>
      <Route index element={s(<ITRPage />)} />
      <Route path=":id" element={s(<ITRDetailPage />)} />
    </Routes>
  );
}

/** Equivalent as data, for a `children:`-style splice under `itr/*`. */
export const itrRoutes: RouteObject[] = [
  { index: true, element: s(<ITRPage />) },
  { path: ":id", element: s(<ITRDetailPage />) },
];

/**
 * `/settings` — admin-only, gated by `isAdminOnlyPath` inside `RequireAuth`.
 * Mount as a child of the guarded `DashboardLayout` route, alongside
 * `no-access`.
 */
export const settingsRoutes: RouteObject[] = [
  { path: "settings", element: s(<SettingsPage />) },
  // `/settings/*` is admin-gated by the same rule; the page reads its tab
  // from `?tab=`, so any deeper path resolves to the same screen rather than
  // falling through to the 404 (which the Next.js app would never show).
  { path: "settings/*", element: s(<SettingsPage />) },
];

/**
 * The routes this slice contributes AS DATA, ready to append to the guarded
 * layout's `children` in `routes.tsx`.
 *
 * `/itr` is deliberately NOT in here: it is already generated from
 * `PAGE_REGISTRY` as `itr/*`, and adding a second matching route would leave
 * two entries competing for one path. Wire ITR up instead by registering
 * `ITRSection` in that file's `IMPLEMENTED` map:
 *
 *     const IMPLEMENTED = { dashboard: <DashboardPage />, itr: <ITRSection /> };
 *
 * `/settings` has no registry row (it is a hard-admin path in `middleware.ts`,
 * not a matrix-gated page), so it does need a route of its own — that is what
 * this array is.
 */
export const routes: RouteObject[] = settingsRoutes;

export default routes;
